import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import ApprovalStatsBar from './components/ApprovalStatsBar';
import ApprovalFilters from './components/ApprovalFilters';
import ApprovalTable from './components/ApprovalTable';
import ReminderSchedulePanel from './components/ReminderSchedulePanel';
import ApprovalDetailModal from './components/ApprovalDetailModal';

const TABS = [
  { id: 'all', label: 'All Approvals', icon: 'LayoutList' },
  { id: 'pending', label: 'Pending', icon: 'Clock' },
  { id: 'approved', label: 'Approved', icon: 'CheckCircle' },
  { id: 'rejected', label: 'Rejected', icon: 'XCircle' },
  { id: 'reminders', label: 'Reminder Schedules', icon: 'Bell' },
];

const ApprovalDashboard = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({ search: '', type: 'all', status: 'all', dateRange: '30d' });
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [sendingReminder, setSendingReminder] = useState(null);

  useEffect(() => {
    fetchApprovals();
  }, [filters?.dateRange]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3500);
  };

  const getDateFilter = () => {
    const now = new Date();
    const map = { '7d': 7, '30d': 30, '90d': 90 };
    const days = map?.[filters?.dateRange];
    if (!days) return null;
    const from = new Date(now);
    from.setDate(from.getDate() - days);
    return from.toISOString();
  };

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const dateFrom = getDateFilter();

      // Fetch ticket approvals
      let ticketQuery = supabase
        ?.from('ticket_customer_approvals')
        ?.select(`
          id, status, customer_name, customer_email, notes, customer_notes,
          created_at, deadline_at, decided_at, customer_signature_name,
          ticket:tickets(ticket_number, id)
        `);
      if (dateFrom) ticketQuery = ticketQuery?.gte('created_at', dateFrom);

      // Fetch quote approvals
      let quoteQuery = supabase
        ?.from('quote_approval_requests')
        ?.select(`
          id, status, approver_name, approver_email, decision_notes,
          created_at, deadline_at, decided_at,
          quote:quotes(quote_number, id, customer:customers(name, email))
        `);
      if (dateFrom) quoteQuery = quoteQuery?.gte('created_at', dateFrom);

      const [ticketRes, quoteRes] = await Promise.all([ticketQuery, quoteQuery]);

      const ticketApprovals = (ticketRes?.data ?? [])?.map(a => ({
        ...a,
        type: 'ticket',
        reference: a?.ticket?.ticket_number ?? '—',
        customer_name: a?.customer_name,
        customer_email: a?.customer_email,
      }));

      const quoteApprovals = (quoteRes?.data ?? [])?.map(a => ({
        ...a,
        type: 'quote',
        reference: a?.quote?.quote_number ?? '—',
        customer_name: a?.quote?.customer?.name ?? a?.approver_name ?? '—',
        customer_email: a?.quote?.customer?.email ?? a?.approver_email ?? '',
        notes: a?.decision_notes,
      }));

      const all = [...ticketApprovals, ...quoteApprovals]?.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setApprovals(all);
      computeStats(all);
    } catch (err) {
      console.error('Error fetching approvals:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [filters?.dateRange]);

  const computeStats = (all) => {
    const now = new Date();
    const pending = all?.filter(a => a?.status === 'pending');
    const overdue = pending?.filter(a => a?.deadline_at && new Date(a.deadline_at) < now);
    const approved = all?.filter(a => a?.status === 'approved');
    const decided = all?.filter(a => a?.status === 'approved' || a?.status === 'rejected');
    const approvalRate = decided?.length > 0 ? Math.round((approved?.length / decided?.length) * 100) : 0;

    // Avg response time in hours
    const responseTimes = decided?.filter(a => a?.decided_at && a?.created_at)?.map(a => (new Date(a.decided_at) - new Date(a.created_at)) / (1000 * 60 * 60));
    const avgHours = responseTimes?.length > 0
      ? (responseTimes?.reduce((s, v) => s + v, 0) / responseTimes?.length)
      : null;
    const avgResponseTime = avgHours !== null
      ? avgHours < 24 ? `${Math.round(avgHours)}h` : `${(avgHours / 24)?.toFixed(1)}d`
      : '—';

    setStats({ pending: pending?.length, overdue: overdue?.length, approved: approved?.length, approvalRate, avgResponseTime });
  };

  const getFilteredApprovals = () => {
    let list = approvals;

    // Tab filter
    if (activeTab !== 'all' && activeTab !== 'reminders') {
      list = list?.filter(a => a?.status === activeTab);
    }

    // Type filter
    if (filters?.type !== 'all') list = list?.filter(a => a?.type === filters?.type);

    // Status filter (from dropdown, overrides tab)
    if (filters?.status !== 'all') list = list?.filter(a => a?.status === filters?.status);

    // Search
    if (filters?.search?.trim()) {
      const q = filters?.search?.toLowerCase();
      list = list?.filter(a =>
        a?.customer_name?.toLowerCase()?.includes(q) ||
        a?.reference?.toLowerCase()?.includes(q) ||
        a?.customer_email?.toLowerCase()?.includes(q)
      );
    }

    return list;
  };

  const handleSendReminder = async (approval) => {
    try {
      setSendingReminder(approval?.id);

      // Log reminder in approval_reminders table
      await supabase?.from('approval_reminders')?.insert([{
        approval_id: approval?.id,
        approval_type: approval?.type,
        recipient_email: approval?.customer_email,
        recipient_name: approval?.customer_name,
        subject: `Reminder: Your approval for ${approval?.reference} is needed`,
        status: 'sent',
        scheduled_at: new Date()?.toISOString(),
        sent_at: new Date()?.toISOString(),
        reminder_count: 1,
      }]);

      // Send email via edge function
      if (approval?.customer_email && approval?.type === 'ticket') {
        const portalUrl = `${window?.location?.origin}/customer-approval-portal?token=${approval?.approval_token ?? ''}`;
        await fetch(
          `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-ticket-approval`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              type: 'ticket_approval_request',
              recipientEmail: approval?.customer_email,
              recipientName: approval?.customer_name,
              ticketNumber: approval?.reference,
              deadline: approval?.deadline_at
                ? new Date(approval.deadline_at)?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                : 'Soon',
              portalUrl,
              approvalToken: approval?.approval_token,
            }),
          }
        );
      }

      showSuccess(`Reminder sent to ${approval?.customer_name}`);
    } catch (err) {
      console.error('Error sending reminder:', err?.message);
      showSuccess('Reminder logged (email delivery may vary)');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleResend = async (approval) => {
    await handleSendReminder(approval);
    showSuccess(`Approval resent to ${approval?.customer_name}`);
  };

  const handleBulkReminders = async () => {
    const overdue = approvals?.filter(
      a => a?.status === 'pending' && a?.deadline_at && new Date(a.deadline_at) < new Date()
    );
    if (overdue?.length === 0) {
      showSuccess('No overdue pending approvals found');
      return;
    }
    for (const approval of overdue) {
      await handleSendReminder(approval);
    }
    showSuccess(`Sent reminders for ${overdue?.length} overdue approval${overdue?.length !== 1 ? 's' : ''}`);
  };

  const filteredApprovals = getFilteredApprovals();
  const overdueCount = approvals?.filter(
    a => a?.status === 'pending' && a?.deadline_at && new Date(a.deadline_at) < new Date()
  )?.length;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-8 px-4 lg:px-6">
        {/* Page Header */}
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Approval Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Centralized oversight of all customer approvals across tickets and quotes
            </p>
          </div>
          <div className="flex items-center gap-3">
            {successMsg && (
              <div className="flex items-center gap-2 px-4 py-2 bg-success/10 border border-success/20 text-success text-sm font-medium rounded-lg">
                <Icon name="CheckCircle" size={14} />
                {successMsg}
              </div>
            )}
            {overdueCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkReminders}
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                <Icon name="Bell" size={14} className="mr-1.5" />
                Send {overdueCount} Overdue Reminder{overdueCount !== 1 ? 's' : ''}
              </Button>
            )}
            <Button size="sm" onClick={fetchApprovals}>
              <Icon name="RefreshCw" size={14} className="mr-1.5" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        <ApprovalStatsBar stats={stats} loading={loading} />

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-xl mb-5 overflow-x-auto">
          {TABS?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab?.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
              {tab?.id === 'pending' && stats?.pending > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full font-semibold">
                  {stats?.pending}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'reminders' ? (
          <div className="bg-card border border-border rounded-xl p-6">
            <ReminderSchedulePanel onSuccess={showSuccess} />
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl">
            <div className="px-5 pt-5">
              <ApprovalFilters filters={filters} onChange={setFilters} />
            </div>
            <div className="px-5 pb-2">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-muted-foreground">
                  {loading ? 'Loading...' : `${filteredApprovals?.length} approval${filteredApprovals?.length !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>
            <ApprovalTable
              approvals={filteredApprovals}
              loading={loading}
              onResend={handleResend}
              onSendReminder={handleSendReminder}
              onViewDetails={setSelectedApproval}
            />
          </div>
        )}
      </main>
      {selectedApproval && (
        <ApprovalDetailModal
          approval={selectedApproval}
          onClose={() => setSelectedApproval(null)}
          onResend={handleResend}
          onSendReminder={handleSendReminder}
        />
      )}
    </div>
  );
};

export default ApprovalDashboard;
